import { Link } from "react-router-dom";
import { COMPANY } from "@/data/site";
import { useSeo } from "@/hooks/useSeo";

const H2 = ({ children }) => <h2 className="font-serif text-2xl md:text-3xl mt-12 mb-4 text-[var(--ink)]">{children}</h2>;
const P = ({ children }) => <p className="text-[var(--muted)] leading-relaxed mb-4">{children}</p>;
const LI = ({ children }) => <li className="text-[var(--muted)] leading-relaxed">{children}</li>;

export default function Cookies() {
  useSeo(
    "Cookie Policy | StoneCrest Exteriors",
    "How StoneCrest Exteriors Specialists Ltd uses cookies and similar technologies on this website, and how you can manage your preferences.",
    "https://stonecrestexteriors.co.uk/cookies"
  );

  return (
    <div className="px-6 md:px-12 max-w-3xl mx-auto py-16 md:py-24" data-testid="cookies-page">
      <p className="text-[11px] uppercase tracking-[0.35em] text-[var(--oxblood)] mb-4">Legal</p>
      <h1 className="font-serif font-light text-5xl md:text-6xl leading-[1.02] tracking-tight text-[var(--ink)]">Cookie Policy</h1>
      <p className="mt-4 text-sm text-[var(--muted)]">Last updated: August 2026</p>

      <div className="mt-10">
        <H2>1. About this policy</H2>
        <P>This Cookie Policy explains how <strong>StoneCrest Exteriors Specialists Ltd</strong>, trading as StoneCrest Exteriors ("we", "us", "our"), uses cookies and similar technologies on this website. It should be read alongside our <Link to="/privacy" className="text-[var(--oxblood)] underline" data-testid="cookies-privacy-link">Privacy Policy</Link>.</P>

        <H2>2. What are cookies?</H2>
        <P>Cookies are small text files placed on your device when you visit a website. They are widely used to make websites work, or work more efficiently, as well as to provide information to the website owner. Similar technologies such as pixels and local storage may also be used for the same purposes.</P>

        <H2>3. Types of cookies we use</H2>
        <P>We use the following categories of cookies:</P>
        <ul className="list-disc pl-6 space-y-2 mb-4">
          <LI><strong>Strictly necessary cookies</strong> — required for the website to function correctly, such as remembering your cookie preferences and keeping the site secure. These cannot be switched off in our systems.</LI>
          <LI><strong>Analytics cookies</strong> — where you consent, we use <strong>Google Analytics</strong> to understand how visitors use our website (for example which pages are viewed and how people arrive at the site). This helps us improve the site. These cookies are only set with your consent.</LI>
        </ul>

        <H2>4. Analytics — Google Analytics</H2>
        <P>Subject to your consent, we use Google Analytics to collect anonymised information about how you use our website. This data helps us measure performance and improve the experience. Google Analytics sets its own cookies; you can read more about how Google uses data at <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer" className="text-[var(--oxblood)] underline">policies.google.com</a>.</P>

        <H2>5. Managing your cookie preferences</H2>
        <P>You can manage or withdraw your consent to non-essential cookies at any time. You can also control cookies through your browser settings — most browsers allow you to refuse or delete cookies. Please note that blocking strictly necessary cookies may affect how the website functions.</P>
        <P>To learn how to manage cookies in your browser, visit <a href="https://www.aboutcookies.org" target="_blank" rel="noopener noreferrer" className="text-[var(--oxblood)] underline">aboutcookies.org</a>.</P>

        <H2>6. Changes to this policy</H2>
        <P>We may update this Cookie Policy from time to time. Any changes will be posted on this page with an updated "Last updated" date above.</P>

        <H2>7. Contact details</H2>
        <div className="text-[var(--muted)] leading-relaxed mb-4">
          <p><strong className="text-[var(--ink)]">StoneCrest Exteriors Specialists Ltd</strong></p>
          <p>Registered in England and Wales</p>
          <p>Company No. 17390567</p>
          <p>Registered Office: The Exchange, Flat 8 Percy Street, Preston, PR1 1AW</p>
          <p>Email: <a href={`mailto:${COMPANY.email}`} className="text-[var(--oxblood)] underline">{COMPANY.email}</a></p>
          <p>Phone: <a href={COMPANY.phoneHref} className="text-[var(--oxblood)] underline">+44 7307 352758</a></p>
        </div>
      </div>
    </div>
  );
}
